import React from "react";
import { Add, Visibility } from "@mui/icons-material";
import { Box, Button, IconButton, Switch } from "@mui/material";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { BaseTabs } from "components/Shared/BaseTabs";
import AppTable from "components/Shared/AppTable";
import FormDrawer from "components/Shared/FormDrawer";
import { useTablePagination } from "hooks";
import BranchTallyUploadAttachmentForm from "./BranchTallyUploadAttachmentForm";
import BranchTallyUploadAttachmentFilter from "./BranchTallyUploadAttachmentFilter";
import { useBranchTallyUploadDetailContext } from "contexts/BranchTallyUploadDetailContext";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useAddBranchTallyUploadAttachmentsMutation, useLazyListBranchTallyUploadAttachmentsQuery, useUpdateBranchTallyUploadAttachmentStatusMutation } from "store/services/branchTallyUploadApi";

const BranchTallyUploadAttachmentTab = () => {
  const { branchTallyUploadDetail } = useBranchTallyUploadDetailContext();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const fromDate = searchParams.get("fromDate") || "";
  const toDate = searchParams.get("toDate") || "";
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [showForm, setShowForm] = React.useState(false);
  const [fetchAttachments, { data, isLoading, isFetching }] = useLazyListBranchTallyUploadAttachmentsQuery();
  const [addAttachments, { isLoading: adding }] = useAddBranchTallyUploadAttachmentsMutation();
  const [updateStatus, { isLoading: updating }] = useUpdateBranchTallyUploadAttachmentStatusMutation();

  const loadAttachments = React.useCallback(() => {
    if (branchTallyUploadDetail?._id) fetchAttachments({ id: branchTallyUploadDetail._id, active: activeTab === "Active" ? "true" : "false", page: currentPage, limit: perPage, ...(keyword && { keyword }), ...(fromDate && { fromDate }), ...(toDate && { toDate }) });
  }, [activeTab, branchTallyUploadDetail?._id, currentPage, fetchAttachments, fromDate, keyword, perPage, toDate]);
  React.useEffect(() => { loadAttachments(); }, [loadAttachments]);

  const handleAdd = async (attachments) => {
    try { await addAttachments({ id: branchTallyUploadDetail._id, attachments }).unwrap(); toast.success("Attachments added successfully!"); setShowForm(false); loadAttachments(); } catch (error) { toast.error(error?.data?.message || "Could not add attachments"); }
  };
  const handleStatus = async (attachment) => {
    try { await updateStatus({ id: branchTallyUploadDetail._id, attachmentId: attachment._id, isActive: !attachment.isActive }).unwrap(); toast.success(`Attachment ${attachment.isActive ? "deactivated" : "activated"}`); loadAttachments(); } catch (error) { toast.error(error?.data?.message || "Could not update attachment"); }
  };

  const columns = [
    { name: "Attachment", selector: (row) => row.name, cell: (row) => <a href={row.url} target="_blank" rel="noreferrer"><IconButton size="small" aria-label="View attachment"><Visibility /></IconButton></a> },
    { name: "Added User", selector: (row) => row.addedBy?.name || row.addedBy?.email || "N/A" },
    { name: "Added Date", selector: (row) => localizeDateTimeShort(row.addedAt) },
    { name: "Status", right: true, cell: (row) => <Switch size="small" checked={row.isActive} onChange={() => handleStatus(row)} inputProps={{ "aria-label": `${row.name || "Attachment"} active status` }} /> },
  ];
  const tabs = [{ label: "Active", value: "Active" }, { label: "Inactive", value: "Inactive" }];

  return <>
    <BaseTabs tabs={tabs} defaultValue={activeTab} actions={<Button startIcon={<Add />} variant="outlined" onClick={() => setShowForm(true)}>Add Attachment</Button>}>
      <BranchTallyUploadAttachmentFilter disabled={isLoading || isFetching || updating} />
      <Box paddingTop={2}><AppTable columns={columns} data={data?.data || []} progressPending={isLoading || isFetching || updating} paginationServer paginationPerPage={perPage} paginationTotalRows={data?.totalItems || 0} paginationDefaultPage={currentPage} onChangePage={handlePageChange} onChangeRowsPerPage={handlePerRowsChange} /></Box>
    </BaseTabs>
    <FormDrawer title="Add Attachment" open={showForm} onClose={() => setShowForm(false)}><BranchTallyUploadAttachmentForm isBusy={adding} onCancel={() => setShowForm(false)} onSubmit={handleAdd} /></FormDrawer>
  </>;
};

export default BranchTallyUploadAttachmentTab;
